// Deep link handler for email verification and other app links
// Handles both custom URL scheme (exzero://) and universal links (https://)

interface DeepLinkParams {
  token?: string;
  mode?: string;
  guestAccountId?: string;
  guestToken?: string;
}

/**
 * Parse deep link URL and extract parameters
 */
function parseDeepLink(url: string): DeepLinkParams | null {
  try {
    // Handle both exzero:// and https:// URLs
    let urlObj: URL;

    if (url.startsWith("exzero://")) {
      // Convert custom scheme to http for parsing
      urlObj = new URL(url.replace("exzero://", "http://"));
    } else {
      urlObj = new URL(url);
    }

    const params: DeepLinkParams = {};

    // Extract query parameters
    params.token = urlObj.searchParams.get("token") || undefined;
    params.mode = urlObj.searchParams.get("mode") || undefined;
    params.guestAccountId =
      urlObj.searchParams.get("guestAccountId") || undefined;
    params.guestToken = urlObj.searchParams.get("guestToken") || undefined;

    return params;
  } catch (err) {
    console.error("[Deep Link] Failed to parse URL:", err);
    return null;
  }
}

/**
 * Call server API to verify email using the token from deep link
 */
async function verifyEmailFromDeepLink(
  params: DeepLinkParams
): Promise<boolean> {
  try {
    const baseUrl =
      (import.meta.env as any).VITE_API_URL ||
      "https://mobile-extraction-server-eu.onrender.com";

    console.info("[Deep Link] Verifying email with server...", {
      mode: params.mode,
      hasToken: !!params.token,
    });

    const response = await fetch(`${baseUrl}/api/verify-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: params.token,
        mode: params.mode,
        guestAccountId: params.guestAccountId,
        guestToken: params.guestToken,
      }),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      console.info("[Deep Link] Email verification successful!", {
        accountId: data.accountId,
        email: data.email,
      });

      // Show success message to user
      if (window.player) {
        // Update player's email verification status
        if (window.player.data) {
          window.player.data.mailVerified = true;
        }
      }

      // For login mode, reload the page to reconnect with the new email account
      if (params.mode === "login") {
        console.info(
          "[Deep Link] Login verification complete, reloading to reconnect with verified account..."
        );
        // Wait a moment to show the success message, then reload
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }

      return true;
    } else {
      console.error("[Deep Link] Email verification failed:", data.error);
      return false;
    }
  } catch (err) {
    console.error("[Deep Link] Network error during verification:", err);
    return false;
  }
}

/**
 * Check if the app was opened via a deep link and handle it
 * Should be called on app startup
 */
export async function handleDeepLink(): Promise<boolean> {
  // Check for Android deep link param
  const urlParams = new URLSearchParams(window.location.search);
  const deeplink = urlParams.get("deeplink");

  let params: DeepLinkParams | null = null;

  if (deeplink) {
    // Android: parse the deep link URL passed from MainActivity
    params = parseDeepLink(deeplink);
  } else {
    // iOS and web: check for token in query or hash
    const token = urlParams.get("token");
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);
    const hashToken = hashParams.get("token");
    const deepLinkToken = token || hashToken;
    if (deepLinkToken) {
      params = {
        token: deepLinkToken,
        mode: urlParams.get("mode") || hashParams.get("mode") || undefined,
        guestAccountId:
          urlParams.get("guestAccountId") ||
          hashParams.get("guestAccountId") ||
          undefined,
        guestToken:
          urlParams.get("guestToken") ||
          hashParams.get("guestToken") ||
          undefined,
      };
    }
  }

  if (params && params.token) {
    console.info("[Deep Link] Detected verification link on app startup");
    const success = await verifyEmailFromDeepLink(params);
    // Clean up URL after processing
    if (window.history && window.history.replaceState) {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    return success;
  }
  return false;
}

/**
 * Initialize deep link listener for iOS
 * iOS may send deep links after the app has already loaded
 */
export function initializeDeepLinkListener() {
  // Listen for custom URL scheme events (iOS)
  window.addEventListener("message", async (event) => {
    if (event.data && event.data.type === "deep-link") {
      console.info("[Deep Link] Received deep link message:", event.data.url);

      const params = parseDeepLink(event.data.url);
      if (params && params.token) {
        await verifyEmailFromDeepLink(params);
      }
    }
  });

  // Listen for app resume events (when app comes to foreground)
  document.addEventListener("resume", async () => {
    console.info("[Deep Link] App resumed, checking for pending deep links");
    await handleDeepLink();
  });

  console.info("[Deep Link] Deep link listener initialized");
}

/**
 * Show verification status to user
 */
export function showVerificationStatus(success: boolean, message?: string) {
  // This can be enhanced to show a nice UI notification
  // For now, we'll use a simple alert or console log

  const defaultMessage = success
    ? "Email verified successfully! You can now access all features."
    : "Email verification failed. Please try again or contact support.";

  const finalMessage = message || defaultMessage;

  console.info("[Deep Link]", finalMessage);

  // If there's a scene with a notification system, use it
  // Otherwise, store the message to be displayed when the game loads
  if (typeof window !== "undefined") {
    (window as any).pendingVerificationMessage = {
      success,
      message: finalMessage,
      timestamp: Date.now(),
    };
  }
}
